// Narration: BuildLab reads directions aloud (decision 90, D48). Shared by the build script that records fixed
// text with Kokoro (tools/narration/build.ts) and the web app's speaker button (apps/web Narrate.tsx).
// Pure functions only: nothing here talks to a network or a model.

/** apps/web/public/narration/manifest.json */
export interface NarrationManifest {
  version: 1;
  /** Text-to-speech engine the clips were recorded with ("kokoro" or "piper"; see tools/narration/engines.ts). */
  engine: string;
  /** The engine's voice and settings, e.g. "af_heart speed 0.95". */
  voice: string;
  /** `${assignmentId}/${stepId}` (or `ui/<name>`) → the recorded clip of `text`. */
  clips: Record<string, NarrationClip>;
}

export interface NarrationClip {
  /** textHash(text) of the words on screen; a clip only plays for text with the same hash. */
  hash: string;
  /** File name in /narration/. */
  file: string;
  text: string;
}

/** Fixed lines the app may say (recorded with the mission steps). Keys are `ui/<name>`. */
export const UI_LINES = {
  "step-done": "Step done! On to the next one.",
  "mission-done": "Mission complete. Nice work!",
  "tool-unlocked": "New tool unlocked!",
  "predict-first": "Before you play, make a guess. What do you think will happen?",
} as const;

/** Whitespace-collapsed text, so line wrapping and spacing don't change a clip's key. */
export function normalizeText(text: string): string {
  return text.replace(/\s+/g, " ").trim();
}

/** Short, stable hash of the normalized text: FNV-1a over UTF-8, 8 hex digits. */
export function textHash(text: string): string {
  const bytes = new TextEncoder().encode(normalizeText(text));
  let h = 0x811c9dc5;
  for (const b of bytes) {
    h ^= b;
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(16).padStart(8, "0");
}

/**
 * Clip file name for a key and its text: "l1-compliment-machine/step-1" → "l1-compliment-machine--step-1-<hash>.mp3".
 * `recordedWith` (engine and voice, like "kokoro af_heart speed 0.95") goes into the hash
 * too, so switching engine or voice records every clip again.
 */
export function clipFile(key: string, text: string, recordedWith = "", ext = "mp3"): string {
  const safe = key.replace(/\//g, "--").replace(/[^a-zA-Z0-9_-]/g, "_");
  return `${safe}-${textHash(recordedWith ? `${recordedWith} | ${text}` : text)}.${ext}`;
}

/**
 * The recorded clip for this text, if there is one: the clip under `key` when its text still
 * matches, else any clip with the same text. Changed text never plays a stale recording.
 */
export function findClip(manifest: NarrationManifest | null | undefined, text: string, key?: string): string | null {
  if (!manifest || !normalizeText(text)) return null;
  const hash = textHash(text);
  const byKey = key ? manifest.clips[key] : undefined;
  if (byKey && byKey.hash === hash) return byKey.file;
  for (const clip of Object.values(manifest.clips)) if (clip.hash === hash) return clip.file;
  return null;
}

/**
 * What the recorded voice says for a line of fixed lesson text: the same words, minus symbols a
 * voice would read out oddly ("## Rules" → "Rules", "PROJECT.md" → "project dot M D", "___" →
 * "blank"). Clip files are hashed over these words, so changing this re-records what it changes.
 */
export function forVoice(text: string): string {
  return normalizeText(
    text
      .replace(/PROJECT\.md/g, "project dot M D")
      .replace(/\b(\w+)\.(js|css|html)\b/g, "$1 dot $2")
      .replace(/getData\("(\w+)"\)/g, "get data $1")
      .replace(/getData/g, "get data")
      .replace(/#{1,6}\s*/g, "")
      .replace(/_{2,}/g, "blank")
      .replace(/\s+\/\s+/g, ", ")
      .replace(/(\d+)\s*[–-]\s*(\d+)/g, "$1 to $2")
      .replace(/(\d+)\+/g, "$1 or more")
      .replace(/°F/g, " degrees")
      .replace(/\.\.\./g, "…")
      // Kokoro mishears (checked with Whisper): "Remixed from" as "remix from", "stuck under Log" as "stuck in her log".
      .replace(/\bRemixed from\b/g, "Remixed-from")
      .replace(/\bgot stuck under\b/g, "got stuck, under"),
  );
}

/** The parts of a SpeechSynthesisVoice that choosing one needs. */
export interface VoiceLike {
  name: string;
  lang: string;
  localService: boolean;
  default: boolean;
}

/**
 * The device voice: an English voice that runs on the device (localService), US English first.
 * Never a network voice (Chrome's "Google …" voices send the words to a server), so a helper reply
 * never leaves the Chromebook. Null when there's no on-device English voice: then nothing is said.
 */
export function chooseVoice<V extends VoiceLike>(voices: readonly V[]): V | null {
  const us = (v: V) => /^en[-_]US$/i.test(v.lang);
  const rank = (v: V) => (us(v) ? 2 : 0) + (v.default ? 1 : 0);
  let best: V | null = null;
  for (const v of voices) if (v.localService && /^en([-_]|$)/i.test(v.lang) && (!best || rank(v) > rank(best))) best = v;
  return best;
}

/** A helper reply as one piece of text to read: its words, then "What I changed" and "Try this" lists. */
export function replyWords(r: { text?: string; changed?: string[]; tryThis?: string[] }): string {
  const parts: string[] = [];
  if (r.text?.trim()) parts.push(r.text.trim());
  if (r.changed?.length) parts.push(["What I changed:", ...r.changed.map((s) => `- ${s}`)].join("\n"));
  if (r.tryThis?.length) parts.push(["Try this:", ...r.tryThis.map((s) => `- ${s}`)].join("\n"));
  return parts.join("\n\n");
}

export const MORE_ON_SCREEN = "…and more on screen.";

/**
 * Turns a helper reply (plain text or Markdown) into words a voice can read: code blocks are left
 * out (they're on screen), inline code keeps its words, links keep their text, Markdown symbols
 * go. Reads at most `max` characters, cut at a sentence or word, then says "…and more on screen."
 */
export function speakable(text: string, max = 600): { text: string; truncated: boolean } {
  let s = text
    .replace(/```[\s\S]*?(```|$)/g, "\n") // fenced code blocks (an unclosed one runs to the end)
    .replace(/~~~[\s\S]*?(~~~|$)/g, "\n")
    .replace(/<[^>\n]+>/g, " ") // HTML tags
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, "$1") // images → alt text
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1") // links → their text
    .replace(/https?:\/\/\S+/g, "a link")
    .replace(/`([^`\n]*)`/g, "$1") // inline code keeps its words
    .replace(/^\s{0,3}#{1,6}\s+/gm, "") // headings
    .replace(/^\s{0,3}>\s?/gm, "") // block quotes
    .replace(/^\s*[-*+]\s+\[[ xX]\]\s+/gm, "") // task boxes
    .replace(/^\s*[-*+]\s+/gm, "") // bullets
    .replace(/^\s*(\d+)[.)]\s+/gm, "$1. ") // numbered lists stay numbered
    .replace(/^\s*([-*_]\s*){3,}$/gm, "") // horizontal rules
    .replace(/^\s*\|?(\s*:?-+:?\s*\|)+\s*:?-*:?\s*$/gm, "") // table rule rows
    .replace(/\|/g, ", ")
    .replace(/(\*\*|__)(.+?)\1/g, "$2") // bold
    .replace(/(^|[\s(])[*_]([^*_\n]+)[*_](?=[\s).,!?:;]|$)/gm, "$1$2") // italics
    .replace(/~~(.+?)~~/g, "$1")
    .replace(/[*#`~]/g, "");
  // Each line is its own sentence, so the voice pauses between list items.
  s = s
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean)
    .map((l) => (/[.!?:;,…]$/.test(l) ? l : `${l}.`))
    .join(" ");
  s = normalizeText(s).replace(/\s+([.,!?;:])/g, "$1").replace(/([.,!?;:])[.,]+/g, "$1");
  if (s.length <= max) return { text: s, truncated: false };
  const head = s.slice(0, max);
  const sentence = Math.max(head.lastIndexOf(". "), head.lastIndexOf("! "), head.lastIndexOf("? "));
  const cut = sentence > max * 0.5 ? head.slice(0, sentence + 1) : head.slice(0, Math.max(head.lastIndexOf(" "), 1)).replace(/[,;:]$/, "");
  return { text: `${cut.trim()} ${MORE_ON_SCREEN}`, truncated: true };
}
